import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { testPrepAPI } from './testPrepApi';

const ACCENT = '#1e3a5f';
const ACCENT_LIGHT = '#2a5080';
const SUCCESS_GREEN = '#27ae60';
const ERROR_RED = '#c0392b';
const WARNING_AMBER = '#f39c12';
const BORDER_GRAY = '#dce1e8';
const BG_LIGHT = '#f4f6f9';
const TEXT_DARK = '#1a1a2e';
const TEXT_MID = '#5a6170';
const WHITE = '#ffffff';

const CHOICE_LETTERS = ['A', 'B', 'C', 'D', 'E'];

const SORT_OPTIONS = [
  { value: 'sequence', label: 'Question Order' },
  { value: 'incorrect_first', label: 'Incorrect First' },
  { value: 'slowest_first', label: 'Slowest First' },
  { value: 'by_category', label: 'By Category' },
  { value: 'by_difficulty', label: 'By Difficulty' },
];

const styles = {
  container: {
    display: 'flex',
    height: '100vh',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: TEXT_DARK,
    background: BG_LIGHT,
  },

  sidebar: {
    width: 300,
    background: WHITE,
    borderRight: `1px solid ${BORDER_GRAY}`,
    display: 'flex',
    flexDirection: 'column',
    flexShrink: 0,
    overflow: 'hidden',
  },
  sidebarHeader: {
    padding: '20px 18px 14px',
    borderBottom: `1px solid ${BORDER_GRAY}`,
    flexShrink: 0,
  },
  sidebarTitle: { fontSize: 16, fontWeight: 700, color: ACCENT, marginBottom: 12 },
  filterRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 10,
  },
  select: {
    flex: 1,
    padding: '8px 10px',
    borderRadius: 6,
    border: `1px solid ${BORDER_GRAY}`,
    fontSize: 13,
    color: TEXT_DARK,
    background: WHITE,
    cursor: 'pointer',
    appearance: 'auto',
  },
  filterToggle: {
    padding: '6px 10px',
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 600,
    border: `1px solid ${BORDER_GRAY}`,
    background: BG_LIGHT,
    cursor: 'pointer',
    color: TEXT_MID,
  },
  filterActive: { background: ERROR_RED, color: WHITE, borderColor: ERROR_RED },
  sidebarList: { flex: 1, overflowY: 'auto' },
  sidebarItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 18px',
    cursor: 'pointer',
    borderBottom: `1px solid ${BG_LIGHT}`,
    transition: 'background 0.1s',
    fontSize: 13,
  },
  sidebarItemActive: { background: '#eaf0f8' },
  sidebarItemNumber: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 700,
    flexShrink: 0,
  },
  sidebarItemCorrect: { background: SUCCESS_GREEN, color: WHITE },
  sidebarItemIncorrect: { background: ERROR_RED, color: WHITE },
  sidebarItemMeta: { flex: 1 },
  sidebarItemCategory: { fontSize: 11, color: TEXT_MID },
  sidebarItemTime: { fontSize: 11, color: TEXT_MID, flexShrink: 0 },

  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 28px',
    background: ACCENT,
    color: WHITE,
    flexShrink: 0,
  },
  topBarTitle: { fontSize: 15, fontWeight: 600 },
  topBarNav: { display: 'flex', gap: 8, alignItems: 'center' },
  jumpInput: {
    width: 52,
    padding: '6px 8px',
    borderRadius: 4,
    border: `1px solid rgba(255,255,255,0.3)`,
    background: 'rgba(255,255,255,0.15)',
    color: WHITE,
    fontSize: 13,
    textAlign: 'center',
  },

  detail: {
    flex: 1,
    overflowY: 'auto',
    padding: '32px 36px',
  },
  stimulus: {
    fontSize: 15,
    lineHeight: 1.7,
    padding: '18px 22px',
    background: WHITE,
    borderRadius: 8,
    border: `1px solid ${BORDER_GRAY}`,
    marginBottom: 24,
  },
  prompt: { fontSize: 16, fontWeight: 600, marginBottom: 20 },

  choicesContainer: { display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 28 },
  choiceRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
    padding: '14px 18px',
    borderRadius: 8,
    border: '2px solid',
    fontSize: 14,
    lineHeight: 1.5,
  },
  choiceDefault: { borderColor: BORDER_GRAY, background: WHITE },
  choiceCorrect: { borderColor: SUCCESS_GREEN, background: '#eafaf1' },
  choiceWrong: { borderColor: ERROR_RED, background: '#fdf0ef' },
  choiceLetter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: '50%',
    fontSize: 13,
    fontWeight: 700,
    flexShrink: 0,
    marginTop: 1,
  },
  choiceExplanation: {
    fontSize: 13,
    color: TEXT_MID,
    marginTop: 8,
    paddingTop: 8,
    borderTop: `1px solid ${BORDER_GRAY}`,
    lineHeight: 1.5,
  },

  timeBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '14px 20px',
    background: WHITE,
    borderRadius: 8,
    border: `1px solid ${BORDER_GRAY}`,
    marginBottom: 24,
    fontSize: 14,
  },
  timeBadge: {
    padding: '4px 10px',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 700,
  },

  expandPanel: {
    background: WHITE,
    borderRadius: 8,
    border: `1px solid ${BORDER_GRAY}`,
    marginBottom: 20,
    overflow: 'hidden',
  },
  expandHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '14px 20px',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    color: ACCENT,
    background: BG_LIGHT,
    userSelect: 'none',
  },
  expandBody: { padding: '16px 20px', fontSize: 14, lineHeight: 1.7 },

  resourceLink: {
    display: 'block',
    padding: '10px 0',
    color: ACCENT,
    textDecoration: 'none',
    fontSize: 14,
    fontWeight: 500,
    borderBottom: `1px solid ${BG_LIGHT}`,
  },

  actionRow: {
    display: 'flex',
    gap: 12,
    marginTop: 24,
    flexWrap: 'wrap',
  },
  btn: {
    padding: '10px 20px',
    borderRadius: 6,
    border: 'none',
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600,
    transition: 'opacity 0.15s',
  },
  btnPrimary: { background: ACCENT, color: WHITE },
  btnSecondary: { background: BG_LIGHT, color: TEXT_DARK, border: `1px solid ${BORDER_GRAY}` },
  btnBookmark: { background: WARNING_AMBER, color: WHITE },
  btnDisabled: { opacity: 0.4, cursor: 'not-allowed' },
  navBtn: {
    padding: '6px 14px',
    borderRadius: 4,
    border: 'none',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    background: 'rgba(255,255,255,0.2)',
    color: WHITE,
  },
};

const TestReviewPlayer = ({
  sceneManifest,
  rendererConfig = {},
  scene,
  feedback,
  children,
}) => {
  const [reviewData, setReviewData] = useState(scene?.review || null);
  const [loading, setLoading] = useState(!scene?.review);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [sortBy, setSortBy] = useState('sequence');
  const [filterIncorrect, setFilterIncorrect] = useState(false);
  const [expandedPanels, setExpandedPanels] = useState({});
  const [bookmarked, setBookmarked] = useState(new Set());
  const [jumpValue, setJumpValue] = useState('');

  const sessionId = scene?.session_id || sceneManifest?.session_id;

  useEffect(() => {
    if (reviewData || !sessionId) return;
    setLoading(true);
    testPrepAPI.getTestReview(sessionId)
      .then((res) => { if (res?.data) setReviewData(res.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [sessionId, reviewData]);

  const allQuestions = useMemo(() => reviewData?.questions || [], [reviewData]);

  const sortedQuestions = useMemo(() => {
    let q = [...allQuestions];
    if (filterIncorrect) q = q.filter((item) => !item.is_correct);

    switch (sortBy) {
      case 'incorrect_first':
        q.sort((a, b) => Number(a.is_correct) - Number(b.is_correct));
        break;
      case 'slowest_first':
        q.sort((a, b) => (b.time_spent_seconds || 0) - (a.time_spent_seconds || 0));
        break;
      case 'by_category':
        q.sort((a, b) => (a.category || '').localeCompare(b.category || ''));
        break;
      case 'by_difficulty':
        q.sort((a, b) => (b.difficulty || 0) - (a.difficulty || 0));
        break;
      default:
        break;
    }
    return q;
  }, [allQuestions, sortBy, filterIncorrect]);

  const currentQ = sortedQuestions[currentIndex] || {};
  const choices = currentQ.choices || [];

  const handleTogglePanel = useCallback((panelId) => {
    setExpandedPanels((prev) => ({ ...prev, [panelId]: !prev[panelId] }));
  }, []);

  const handleBookmark = useCallback(() => {
    setBookmarked((prev) => {
      const next = new Set(prev);
      if (next.has(currentQ.id)) next.delete(currentQ.id);
      else next.add(currentQ.id);
      return next;
    });
  }, [currentQ.id]);

  const handleJump = useCallback((e) => {
    if (e.key === 'Enter') {
      const n = parseInt(jumpValue, 10);
      if (n >= 1 && n <= sortedQuestions.length) {
        setCurrentIndex(n - 1);
        setJumpValue('');
      }
    }
  }, [jumpValue, sortedQuestions.length]);

  const handlePrev = useCallback(() => {
    if (currentIndex > 0) setCurrentIndex((i) => i - 1);
  }, [currentIndex]);

  const handleNext = useCallback(() => {
    if (currentIndex < sortedQuestions.length - 1) setCurrentIndex((i) => i + 1);
  }, [currentIndex, sortedQuestions.length]);

  if (loading) {
    return (
      <div style={{ ...styles.container, alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: TEXT_MID, fontSize: 16, fontWeight: 600 }}>
          Loading review data...
        </div>
      </div>
    );
  }

  const timeColor = (currentQ.time_spent_seconds || 0) > (currentQ.expected_seconds || 90) * 1.3
    ? ERROR_RED
    : (currentQ.time_spent_seconds || 0) > (currentQ.expected_seconds || 90)
      ? WARNING_AMBER
      : SUCCESS_GREEN;

  return (
    <div style={styles.container}>
      <div style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <div style={styles.sidebarTitle}>Questions ({sortedQuestions.length})</div>
          <div style={styles.filterRow}>
            <select
              style={styles.select}
              value={sortBy}
              onChange={(e) => { setSortBy(e.target.value); setCurrentIndex(0); }}
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <button
            style={{
              ...styles.filterToggle,
              ...(filterIncorrect ? styles.filterActive : {}),
            }}
            onClick={() => { setFilterIncorrect((v) => !v); setCurrentIndex(0); }}
          >
            {filterIncorrect ? '✗ Incorrect Only' : 'Show All'}
          </button>
        </div>

        <div style={styles.sidebarList}>
          {sortedQuestions.map((q, i) => (
            <div
              key={q.id || i}
              style={{
                ...styles.sidebarItem,
                ...(i === currentIndex ? styles.sidebarItemActive : {}),
              }}
              onClick={() => setCurrentIndex(i)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if (e.key === 'Enter') setCurrentIndex(i); }}
            >
              <div
                style={{
                  ...styles.sidebarItemNumber,
                  ...(q.is_correct ? styles.sidebarItemCorrect : styles.sidebarItemIncorrect),
                }}
              >
                {q.sequence_number || i + 1}
              </div>
              <div style={styles.sidebarItemMeta}>
                <div style={{ fontWeight: 500 }}>
                  {q.category || 'General'}
                </div>
                <div style={styles.sidebarItemCategory}>
                  {q.difficulty_label || `Difficulty: ${q.difficulty || '—'}`}
                </div>
              </div>
              <div style={styles.sidebarItemTime}>
                {q.time_spent_seconds ? `${q.time_spent_seconds}s` : '—'}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={styles.main}>
        <div style={styles.topBar}>
          <span style={styles.topBarTitle}>
            Question {currentIndex + 1} of {sortedQuestions.length}
            {currentQ.category && ` — ${currentQ.category}`}
          </span>
          <div style={styles.topBarNav}>
            <button
              style={{ ...styles.navBtn, ...(currentIndex === 0 ? styles.btnDisabled : {}) }}
              onClick={handlePrev}
              disabled={currentIndex === 0}
            >
              ← Prev
            </button>
            <input
              style={styles.jumpInput}
              type="text"
              placeholder="#"
              value={jumpValue}
              onChange={(e) => setJumpValue(e.target.value)}
              onKeyDown={handleJump}
              aria-label="Jump to question number"
            />
            <button
              style={{ ...styles.navBtn, ...(currentIndex >= sortedQuestions.length - 1 ? styles.btnDisabled : {}) }}
              onClick={handleNext}
              disabled={currentIndex >= sortedQuestions.length - 1}
            >
              Next →
            </button>
          </div>
        </div>

        <div style={styles.detail}>
          {currentQ.stimulus && (
            <div style={styles.stimulus}>{currentQ.stimulus}</div>
          )}

          {(currentQ.stem || currentQ.prompt) && (
            <div style={styles.prompt}>{currentQ.stem || currentQ.prompt}</div>
          )}

          <div style={styles.choicesContainer}>
            {choices.map((choice, i) => {
              const isCorrect = choice.is_correct;
              const isLearnerChoice = choice.id === (currentQ.learner_choice_id || currentQ.selected_choice_id);
              const isWrong = isLearnerChoice && !isCorrect;

              let rowStyle = styles.choiceDefault;
              let letterBg = BG_LIGHT;
              let letterColor = TEXT_MID;

              if (isCorrect) {
                rowStyle = styles.choiceCorrect;
                letterBg = SUCCESS_GREEN;
                letterColor = WHITE;
              } else if (isWrong) {
                rowStyle = styles.choiceWrong;
                letterBg = ERROR_RED;
                letterColor = WHITE;
              }

              return (
                <div key={choice.id || i} style={{ ...styles.choiceRow, ...rowStyle }}>
                  <span style={{ ...styles.choiceLetter, background: letterBg, color: letterColor }}>
                    {CHOICE_LETTERS[i] || i + 1}
                  </span>
                  <div style={{ flex: 1 }}>
                    <div>
                      {choice.text}
                      {isCorrect && <span style={{ marginLeft: 8, color: SUCCESS_GREEN, fontWeight: 700 }}>✓ Correct</span>}
                      {isWrong && <span style={{ marginLeft: 8, color: ERROR_RED, fontWeight: 700 }}>✗ Your Answer</span>}
                      {isLearnerChoice && isCorrect && <span style={{ marginLeft: 8, color: SUCCESS_GREEN, fontWeight: 700 }}> (Your Answer)</span>}
                    </div>
                    {choice.explanation && (
                      <div style={styles.choiceExplanation}>{choice.explanation}</div>
                    )}
                    {!isCorrect && choice.distractor_type && (
                      <div style={{
                        fontSize: 11, color: '#6366f1', fontStyle: 'italic', marginTop: 4,
                      }}>
                        Common trap: {choice.distractor_type.replace(/_/g, ' ')}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {(currentQ.explanation || (currentQ.question && currentQ.question.explanation)) && (
            <div style={{
              padding: '16px 20px', background: '#f0fdf4', borderRadius: 8,
              border: `1px solid #bbf7d0`, marginBottom: 20,
              fontSize: 14, lineHeight: 1.7, color: '#15803d',
            }}>
              <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 13 }}>Correct Answer Rationale</div>
              {currentQ.explanation || currentQ.question?.explanation}
            </div>
          )}

          {!currentQ.is_correct && (
            <div style={{
              ...styles.expandPanel,
              border: `2px solid ${ERROR_RED}20`,
            }}>
              <div
                style={{ ...styles.expandHeader, background: '#fef2f2', color: ERROR_RED }}
                onClick={() => handleTogglePanel('self_explain')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') handleTogglePanel('self_explain'); }}
              >
                <span>Why was my answer wrong?</span>
                <span>{expandedPanels.self_explain ? '▲' : '▼'}</span>
              </div>
              {expandedPanels.self_explain && (
                <div style={styles.expandBody}>
                  <div style={{ fontWeight: 600, marginBottom: 8 }}>Reflect on your reasoning:</div>
                  {(() => {
                    const learnerChoice = choices.find(
                      (c) => c.id === (currentQ.learner_choice_id || currentQ.learner_answer || currentQ.selected_choice_id)
                    );
                    const correctChoice = choices.find((c) => c.is_correct);
                    return (
                      <>
                        {learnerChoice && (
                          <div style={{ marginBottom: 12, padding: '10px 14px', background: '#fef2f2', borderRadius: 6 }}>
                            <div style={{ fontWeight: 600, marginBottom: 4, color: ERROR_RED }}>
                              Your answer ({CHOICE_LETTERS[choices.indexOf(learnerChoice)] || '?'}): {learnerChoice.text}
                            </div>
                            {learnerChoice.explanation && (
                              <div style={{ fontSize: 13, marginBottom: 4 }}>{learnerChoice.explanation}</div>
                            )}
                            {learnerChoice.distractor_type && (
                              <div style={{ fontSize: 11, color: '#6366f1', fontStyle: 'italic' }}>
                                This is a common trap: {learnerChoice.distractor_type.replace(/_/g, ' ')}
                              </div>
                            )}
                          </div>
                        )}
                        {correctChoice && (
                          <div style={{ marginBottom: 12, padding: '10px 14px', background: '#f0fdf4', borderRadius: 6 }}>
                            <div style={{ fontWeight: 600, marginBottom: 4, color: SUCCESS_GREEN }}>
                              Correct answer ({CHOICE_LETTERS[choices.indexOf(correctChoice)] || '?'}): {correctChoice.text}
                            </div>
                            {correctChoice.explanation && (
                              <div style={{ fontSize: 13 }}>{correctChoice.explanation}</div>
                            )}
                          </div>
                        )}
                        {(currentQ.question?.reasoning_prompt || currentQ.reasoning_prompt) && (
                          <div style={{
                            marginTop: 8, padding: '8px 12px', background: '#ddd6fe',
                            borderRadius: 4, fontSize: 12, color: '#4c1d95',
                          }}>
                            <strong>Key insight:</strong> {currentQ.question?.reasoning_prompt || currentQ.reasoning_prompt}
                          </div>
                        )}
                        <div style={{ marginTop: 12, fontSize: 13, color: TEXT_MID, fontStyle: 'italic' }}>
                          Compare your reasoning with the correct answer above. What assumption led you astray?
                          Recognizing this pattern will help you avoid similar traps on the real test.
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          )}

          <div style={styles.timeBox}>
            <span style={{ fontWeight: 600 }}>Time Spent:</span>
            <span>{currentQ.time_spent_seconds || 0}s</span>
            <span style={{ color: TEXT_MID }}>Expected: {currentQ.expected_seconds || 90}s</span>
            <span style={{ ...styles.timeBadge, background: timeColor, color: WHITE }}>
              {(currentQ.time_spent_seconds || 0) <= (currentQ.expected_seconds || 90)
                ? 'On Pace'
                : (currentQ.time_spent_seconds || 0) <= (currentQ.expected_seconds || 90) * 1.3
                  ? 'Slightly Over'
                  : 'Over Time'}
            </span>
          </div>

          {currentQ.teaching_content && (
            <div style={styles.expandPanel}>
              <div
                style={styles.expandHeader}
                onClick={() => handleTogglePanel('teaching')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') handleTogglePanel('teaching'); }}
              >
                <span>Concept Deep-Dive</span>
                <span>{expandedPanels.teaching ? '▲' : '▼'}</span>
              </div>
              {expandedPanels.teaching && (
                <div style={styles.expandBody}>
                  {currentQ.teaching_content.socratic_questions && (
                    <div style={{ marginBottom: 16 }}>
                      <div style={{ fontWeight: 600, marginBottom: 8 }}>Think about it:</div>
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {currentQ.teaching_content.socratic_questions.map((sq, i) => (
                          <li key={i} style={{ marginBottom: 6 }}>{sq}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {currentQ.teaching_content.einstein_layers && (
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 8 }}>Einstein Layers:</div>
                      {currentQ.teaching_content.einstein_layers.map((layer, i) => (
                        <div key={i} style={{ marginBottom: 12, padding: '10px 14px', background: BG_LIGHT, borderRadius: 6 }}>
                          <div style={{ fontWeight: 600, fontSize: 13, color: ACCENT, marginBottom: 4 }}>
                            {layer.title || `Layer ${i + 1}`}
                          </div>
                          <div style={{ fontSize: 14 }}>{layer.content}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {currentQ.resources && currentQ.resources.length > 0 && (
            <div style={styles.expandPanel}>
              <div
                style={styles.expandHeader}
                onClick={() => handleTogglePanel('resources')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') handleTogglePanel('resources'); }}
              >
                <span>Study Resources</span>
                <span>{expandedPanels.resources ? '▲' : '▼'}</span>
              </div>
              {expandedPanels.resources && (
                <div style={styles.expandBody}>
                  {currentQ.resources.map((res, i) => (
                    <a
                      key={i}
                      href={res.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={styles.resourceLink}
                    >
                      {res.title || res.url}
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={styles.actionRow}>
            <button
              style={{
                ...styles.btn,
                ...(bookmarked.has(currentQ.id) ? styles.btnBookmark : styles.btnSecondary),
              }}
              onClick={handleBookmark}
            >
              {bookmarked.has(currentQ.id) ? '★ Bookmarked' : '☆ Bookmark for Study'}
            </button>
            <button
              style={{ ...styles.btn, ...styles.btnPrimary }}
              onClick={() => scene?.onOpenChat?.(currentQ)}
            >
              Ask the Coach
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestReviewPlayer;
